/**
 * Trunnion AI - UI Store
 * Manages UI state: sidebar, theme, modals, layout preferences, favorites, and command palette.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface FavoriteItem {
  type: string;
  id: string;
  label: string;
}

interface UIState {
  sidebarCollapsed: boolean;
  sidebarWidth: number;
  theme: 'dark' | 'light';
  density: 'compact' | 'comfortable' | 'spacious';
  activeModal: string | null;
  modalData: Record<string, unknown> | null;
  inspectorOpen: boolean;
  paletteOpen: boolean;
  commandPaletteOpen: boolean;
  tourCompleted: boolean;
  recentSearches: string[];
  favorites: FavoriteItem[];

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarWidth: (width: number) => void;
  setTheme: (theme: 'dark' | 'light') => void;
  setDensity: (density: 'compact' | 'comfortable' | 'spacious') => void;
  openModal: (id: string, data?: Record<string, unknown>) => void;
  closeModal: () => void;
  setInspectorOpen: (open: boolean) => void;
  setPaletteOpen: (open: boolean) => void;
  setCommandPaletteOpen: (open: boolean) => void;
  toggleCommandPalette: () => void;
  setTourCompleted: (completed: boolean) => void;
  addRecentSearch: (query: string) => void;
  toggleFavorite: (type: string, id: string, label: string) => void;
  isFavorite: (type: string, id: string) => boolean;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      sidebarWidth: 260,
      theme: 'dark',
      density: 'comfortable',
      activeModal: null,
      modalData: null,
      inspectorOpen: true,
      paletteOpen: true,
      commandPaletteOpen: false,
      tourCompleted: false,
      recentSearches: [],
      favorites: [],

      toggleSidebar: () =>
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),

      setSidebarCollapsed: (collapsed) =>
        set({ sidebarCollapsed: collapsed }),

      setSidebarWidth: (width) =>
        set({ sidebarWidth: width }),

      setTheme: (theme) =>
        set({ theme }),

      setDensity: (density) =>
        set({ density }),

      openModal: (id, data) =>
        set({ activeModal: id, modalData: data ?? null }),

      closeModal: () =>
        set({ activeModal: null, modalData: null }),

      setInspectorOpen: (open) =>
        set({ inspectorOpen: open }),

      setPaletteOpen: (open) =>
        set({ paletteOpen: open }),

      setCommandPaletteOpen: (open) =>
        set({ commandPaletteOpen: open }),

      toggleCommandPalette: () =>
        set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

      setTourCompleted: (completed) =>
        set({ tourCompleted: completed }),

      addRecentSearch: (query) =>
        set((state) => {
          const filtered = state.recentSearches.filter((s) => s !== query);
          return { recentSearches: [query, ...filtered].slice(0, 10) };
        }),

      toggleFavorite: (type, id, label) =>
        set((state) => {
          const exists = state.favorites.some(
            (f) => f.type === type && f.id === id,
          );
          if (exists) {
            return {
              favorites: state.favorites.filter(
                (f) => !(f.type === type && f.id === id),
              ),
            };
          }
          return { favorites: [...state.favorites, { type, id, label }] };
        }),

      isFavorite: (type, id) =>
        get().favorites.some((f) => f.type === type && f.id === id),
    }),
    {
      name: 'trunnion-ui-preferences',
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        sidebarWidth: state.sidebarWidth,
        theme: state.theme,
        density: state.density,
        inspectorOpen: state.inspectorOpen,
        paletteOpen: state.paletteOpen,
        tourCompleted: state.tourCompleted,
        recentSearches: state.recentSearches,
        favorites: state.favorites,
      }),
    },
  ),
);
